import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, updateUserProfile } from '../../services/supabaseService';
import { useNavigate } from 'react-router-dom';
import { ExtendedUserProfile } from '../../types/user';
import BaseProfilePage from '../../components/mypage/BaseProfilePage';
import { logout } from '../../services/kakaoAuthService';
import Header from '../../components/Header';
import {
  inputStyles,
  disabledInputStyles,
  sectionTitleStyles,
  sectionDescriptionStyles,
  buttonStyles,
  stackSpacing,
  sectionSpacing,
} from '../../styles/profileStyles';

const ACCENT_COLOR = '#ff6b35';
const AREAS = ['서울', '경기', '인천', '대전', '대구', '부산', '울산', '광주', '세종', '강원', '충북', '충남', '경북', '경남', '전북', '전남', '제주'];
const MAX_AREAS = 3;

const ReviewerEditProfilePage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editedProfile, setEditedProfile] = useState<Partial<ExtendedUserProfile>>({
    displayName: '',
    email: '',
    phoneNumber: '',
    gender: '',
    activityArea: [],
    address: {
      zipCode: '',
      address1: '',
      address2: ''
    },
    bankAccount: {
      accountHolder: '',
      bankName: '',
      accountNumber: ''
    }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (currentUser?.uid) {
          const userProfile = await getUserProfile(currentUser.uid);
          setEditedProfile({
            ...userProfile,
            phoneNumber: userProfile.phoneNumber || '',
            gender: userProfile.gender || '',
            activityArea: userProfile.activityArea || [],
            address: userProfile.address || {
              zipCode: '',
              address1: '',
              address2: ''
            },
            bankAccount: userProfile.bankAccount || {
              accountHolder: '',
              bankName: '',
              accountNumber: ''
            }
          });
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setSnackbar({
          open: true,
          message: '프로필을 불러오는데 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (currentUser) {
        await updateUserProfile(currentUser.uid, {
          ...editedProfile,
          userType: 'reviewer',
          updatedAt: Date.now()
        });

        setSnackbar({
          open: true,
          message: '프로필이 성공적으로 업데이트되었습니다.',
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/mypage/reviewer');
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setSnackbar({
        open: true,
        message: '프로필 업데이트에 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | string[] | { zipCode?: string, address1?: string, address2?: string } | { accountHolder?: string, bankName?: string, accountNumber?: string }) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBankAccountChange = (field: string, value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      bankAccount: {
        ...prev.bankAccount,
        [field]: value
      }
    }));
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1) {
      try {
        setLoading(true);
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('로그아웃 실패:', error);
        setSnackbar({
          open: true,
          message: '로그아웃에 실패했습니다.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } else if (newValue === 2) {
      const confirmed = window.confirm('정말로 탈퇴하시겠습니까?');
      if (confirmed) {
        try {
          // 회원탈퇴 로직
          navigate('/');
        } catch (error) {
          console.error('회원탈퇴 실패:', error);
        }
      }
    } else {
      setTabValue(newValue);
    }
  };

  const handleAreaToggle = (area: string) => {
    const currentAreas = editedProfile.activityArea || [];
    const newAreas = currentAreas.includes(area)
      ? currentAreas.filter(a => a !== area)
      : currentAreas.length < MAX_AREAS
        ? [...currentAreas, area]
        : currentAreas;
    handleInputChange('activityArea', newAreas);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const renderContent = () => (
    <Stack spacing={sectionSpacing} sx={{ pt: 3, pb: 4 }}>
      {/* 기본정보 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          기본정보
        </Typography>
        <Stack spacing={stackSpacing}>
          <TextField
            fullWidth
            label="아이디(이메일)"
            value={editedProfile.email || ''}
            disabled
            sx={disabledInputStyles}
          />
          <TextField
            fullWidth
            label="닉네임"
            value={editedProfile.displayName || ''}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="휴대폰 번호"
            value={editedProfile.phoneNumber || ''}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            sx={inputStyles}
          />
          <Box>
            <Typography sx={{ fontSize: '13px', color: '#666666', mb: 1 }}>성별</Typography>
            <ToggleButtonGroup
              value={editedProfile.gender}
              exclusive
              onChange={(e, value) => handleInputChange('gender', value)}
              sx={{
                width: '100%',
                height: '48px',
                '& .MuiToggleButton-root': {
                  flex: 1,
                  fontSize: '15px',
                  color: '#666666',
                  '&.Mui-selected': {
                    color: '#ffffff',
                    backgroundColor: ACCENT_COLOR,
                    '&:hover': {
                      backgroundColor: ACCENT_COLOR
                    }
                  }
                }
              }}
            >
              <ToggleButton value="male">남성</ToggleButton>
              <ToggleButton value="female">여성</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: '#e0e0e0' }} />

      {/* 활동 지역 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          활동 지역
        </Typography>
        <Typography sx={sectionDescriptionStyles}>
          최대 {MAX_AREAS}개까지 선택 가능합니다.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {AREAS.map((area) => (
            <Chip
              key={area}
              label={area}
              onClick={() => handleAreaToggle(area)}
              sx={{
                height: '36px',
                fontSize: '15px',
                color: editedProfile.activityArea?.includes(area) ? '#ffffff' : '#666666',
                backgroundColor: editedProfile.activityArea?.includes(area) ? ACCENT_COLOR : '#f5f5f5',
                '&:hover': {
                  backgroundColor: editedProfile.activityArea?.includes(area) ? ACCENT_COLOR : '#e0e0e0'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#e0e0e0' }} />

      {/* 계좌정보 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          계좌정보
        </Typography>
        <Stack spacing={stackSpacing}>
          <TextField
            fullWidth
            label="예금주"
            value={editedProfile.bankAccount?.accountHolder || ''}
            onChange={(e) => handleBankAccountChange('accountHolder', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="은행명"
            value={editedProfile.bankAccount?.bankName || ''}
            onChange={(e) => handleBankAccountChange('bankName', e.target.value)}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="계좌번호"
            value={editedProfile.bankAccount?.accountNumber || ''}
            onChange={(e) => handleBankAccountChange('accountNumber', e.target.value)}
            helperText="'-' 없이 입력해주세요"
            sx={inputStyles}
          />
        </Stack>
      </Box>

      <Divider sx={{ borderColor: '#e0e0e0' }} />

      {/* 주소 섹션 */}
      <Box>
        <Typography sx={sectionTitleStyles}>
          주소
        </Typography>
        <Stack spacing={stackSpacing}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="우편번호"
              value={editedProfile.address?.zipCode || ''}
              onChange={(e) => {
                const updatedAddress = {
                  ...(editedProfile.address || {}),
                  zipCode: e.target.value
                };
                handleInputChange('address', updatedAddress);
              }}
              sx={inputStyles}
            />
            <Button
              variant="outlined"
              sx={buttonStyles(ACCENT_COLOR)}
              style={{ minWidth: '100px' }}
            >
              찾기
            </Button>
          </Box>
          <TextField
            fullWidth
            label="주소"
            value={editedProfile.address?.address1 || ''}
            onChange={(e) => {
              const updatedAddress = {
                ...(editedProfile.address || {}),
                address1: e.target.value
              };
              handleInputChange('address', updatedAddress);
            }}
            sx={inputStyles}
          />
          <TextField
            fullWidth
            label="상세주소"
            value={editedProfile.address?.address2 || ''}
            onChange={(e) => {
              const updatedAddress = {
                ...(editedProfile.address || {}),
                address2: e.target.value
              };
              handleInputChange('address', updatedAddress);
            }}
            sx={inputStyles}
          />
        </Stack>
      </Box>

      {/* 저장 버튼 */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        sx={buttonStyles(ACCENT_COLOR)}
      >
        저장
      </Button>
    </Stack>
  );

  return (
    <BaseProfilePage
      title="내 계정"
      backUrl="/mypage/reviewer"
      tabValue={tabValue}
      onTabChange={handleTabChange}
      accentColor={ACCENT_COLOR}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar({ ...snackbar, open: false })}
    >
      {renderContent()}
    </BaseProfilePage>
  );
};

export default ReviewerEditProfilePage; 